import Link from 'next/link';
import Image from 'next/image';

import MapPinIcon from '../Icons/MapPin';
import Bed from '../Icons/Bed';
import Bath from '../Icons/Bath';

import {cn} from '@/lib/utils';
import {IRegionZoneDictonary} from '@/dictionaries/utils/region-zone/types';
import guests from '../../../public/guests.png';
import {villagesMap, sicilyMap} from '@/utils/maps';
import {BlogRelatedVillage} from '@/types/Blog';
import getMediaFilterUrl from '@/utils/get-media-filter';
import CreateMediaFilterTrigger from '@/components/Media/CreateMediaFilterTrigger';

type Props = {
  featuredVillage: BlogRelatedVillage;
  dict: IRegionZoneDictonary;
  lang: string;
};

export default function BlogFeaturedVillagesCard({
  featuredVillage,
  dict,
  lang,
}: Props) {
  const coverImage = featuredVillage.media.find((image) => {
    return image.type === 'cover';
  });

  const filterName = 'villa_card_2x_webp';

  const filteredCoverImage = getMediaFilterUrl({
    mediaFilters: coverImage?.mediaFilters || [],
    filterName,
  });

  const villageSubtitle = `${featuredVillage.city} (${
    dict[featuredVillage.zone.code]
  })`;

  function getVillageLink() {
    let baseUrl = '';

    if (lang !== 'en') {
      baseUrl = `/${lang}`;
    }

    return `${baseUrl}/${sicilyMap[lang]}/${villagesMap[lang]}/${featuredVillage.slug}`;
  }

  return (
    <Link
      className="bg-white shadow-default inline-block outline-none overflow-hidden relative w-4/5 desktop:w-[calc(100%-20px)] cursor-pointer"
      href={getVillageLink()}>
      <CreateMediaFilterTrigger
        imageUrl={filteredCoverImage}
        mediaId={coverImage?.id}
        filterName={filterName}
      />
      <div className="h-[320px] w-full relative overflow-hidden">
        <div className="h-[320px] absolute w-full bottom-0 left-0 bg-cover bg-[50%]">
          <Image
            src={
              filteredCoverImage ??
              `${process.env.NEXT_PUBLIC_S3_URI}/thumbs/${filterName}/${coverImage?.path}${coverImage?.filename}`
            }
            alt={featuredVillage.title}
            fill
            className="block w-full h-full object-cover align-middle max-w-full"
            sizes="(min-width: 1280px) 80vw, (min-width: 767px) 90vw, (min-width: 480px) 100vw, 100vw"
            style={{
              objectFit: 'cover',
            }}
          />
        </div>
      </div>
      <div className="bg-white text-black p-[15px] pb-[5px]">
        <div className="flex flex-col">
          <div className="text-left float-left">
            <h3 className="leading-[1.25] mb-2 text-medium font-title">
              {featuredVillage.title}
            </h3>
            <div>
              <span className="text-base flex items-center">
                <MapPinIcon className="w-5 h-5 mr-1 text-gray-500" />
                {villageSubtitle}
              </span>
            </div>
          </div>
        </div>
        <div className="flex flex-col">
          <ul
            className={cn('mx-[-12px] list-none py-[4px] leading-[1.6] flex')}>
            <li className="flex items-center mx-[12px]">
              <Image
                width={20}
                height={26}
                src={guests}
                alt="Guests icon image"
              />
              <span className="ml-[4px]">{featuredVillage.sleeps}</span>
            </li>
            <li className="flex items-center mx-[12px]">
              <Bath className="w-5 h-5" />
              <span className="ml-[4px]">{featuredVillage.bedrooms}</span>
            </li>
            <li className="flex items-center mx-[12px]">
              <Bed className="w-5 h-5" />
              <span className="ml-[4px]">{featuredVillage.bathrooms}</span>
            </li>
          </ul>
        </div>
      </div>
    </Link>
  );
}
